import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {BaseRenameRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/BaseRenameRequest';
import {QueryCondition} from '@mintdata-wsp/common/controllers/network/generated/requests/QueryCondition';
import {QuerySettings} from '@mintdata-wsp/common/controllers/network/generated/requests/QuerySettings';
import {RowsAddRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RowsAddRequest';
import {RowsDeleteByIdsRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RowsDeleteByIdsRequest';
import {RowsQueryRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RowsQueryRequest';
import {RowsSelectRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RowsSelectRequest';
import {RowsUpdateByIdsRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RowsUpdateByIdsRequest';
import {RowsUpdateByQueryRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RowsUpdateByQueryRequest';
import {RowsUpsertRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RowsUpsertRequest';
import {TableRenameRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/TableRenameRequest';
import {TableSpec} from '@mintdata-wsp/common/controllers/network/generated/requests/TableSpec';
import {TableSpecRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/TableSpecRequest';
import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';

export class BasesController
{
    @bind
    private _currentContext():{organizationSid:string, environmentName:string}
    {
        const {context} = commonControllers;

        // todo: make environmentName configurable
        const organizationSid = context.currentOrganizationSid();
        const environmentName = 'default';

        return {organizationSid, environmentName};
    }

    @bind
    public baseList():Promise<IFrame | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.bases.listBases(organizationSid, environmentName)
            .then(this.objectsCallback(
                resp => resp.bases,
                ['name', 'createdAt', 'createdBy'],
                ['base_name', 'created_at', 'created_by']
            ));
    }

    @bind
    public baseAdd(baseName:string):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.bases.createBase(organizationSid, environmentName, baseName)
            .then(this.booleanCallback);
    }

    @bind
    public baseDelete(baseName:string):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.bases.deleteBase(organizationSid, environmentName, baseName)
            .then(this.booleanCallback);
    }

    @bind
    public baseRename(baseName:string, newBaseName:string):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.bases.renameBase(organizationSid, environmentName, baseName, new BaseRenameRequest(newBaseName))
            .then(this.booleanCallback);
    }

    @bind
    public tableList(baseName:string):Promise<IFrame | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();

        const listPromise = baseName
            ? API.tables.listTablesByBase(organizationSid, environmentName, baseName)
            : API.tables.listTablesByEnvironment(organizationSid, environmentName);

        return listPromise
            .then(this.objectsCallback(
                resp => resp.tables,
                ['baseName', 'name', 'createdAt', 'createdBy'],
                ['base_name', 'table_name', 'created_at', 'created_by']
            ));
    }

    @bind
    public tableGet(baseName:string, tableName:string):Promise<IFrame | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();

        return API.tables.getTableSchema(organizationSid, environmentName, baseName, tableName)
            .then(this.objectsCallback(
                resp => resp.columns,
                ['name', 'type', 'unique'],
                ['column_name', 'column_type', 'unique']
            ));
    }

    @bind
    public tableSpec(baseName:string, tableName:string, tableSpec:TableSpec):Promise<IFrame | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();

        return API.tables.specTable(organizationSid, environmentName, baseName, tableName, new TableSpecRequest(tableSpec))
            .then(this.objectsCallback(
                resp => resp.columns,
                ['name', 'type', 'unique'],
                ['column_name', 'column_type', 'unique']
            ));
    }

    @bind
    public tableRename(baseName:string, tableName:string, newTableName:string):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();

        return API.tables.renameTable(organizationSid, environmentName, baseName, tableName, new TableRenameRequest(newTableName))
            .then(this.booleanCallback);
    }

    @bind
    public tableDelete(baseName:string, tableName:string):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();

        return API.tables.deleteTable(organizationSid, environmentName, baseName, tableName)
            .then(this.booleanCallback);
    }

    @bind
    public tableAddRows(baseName:string, tableName:string, dataFrame:IFrame):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.addRows(organizationSid, environmentName, baseName, tableName, new RowsAddRequest(dataFrame))
            .then(this.booleanCallback);
    }

    @bind
    public tableUpsertRows(baseName:string, tableName:string, upsertColumnName:string, dataFrame:IFrame):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.upsertRows(organizationSid, environmentName, baseName, tableName, new RowsUpsertRequest(upsertColumnName, dataFrame))
            .then(this.booleanCallback);
    }

    @bind
    public tableSelectRows(baseName:string, tableName:string, query:QueryCondition, settings:QuerySettings):Promise<IFrame | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.selectRows(organizationSid, environmentName, baseName, tableName, new RowsSelectRequest(query, settings))
            .then(this.frameCallback(response => response.data));
    }

    @bind
    public tableCountRows(baseName:string, tableName:string, query:QueryCondition):Promise<AInteger | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.countRows(organizationSid, environmentName, baseName, tableName, new RowsQueryRequest(query))
            .then(this.integerCallback(response => response.count));
    }

    @bind
    public deleteRowsByIds(baseName:string, tableName:string, rowIds:Array<number>):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.deleteRowsByIds(organizationSid, environmentName, baseName, tableName, new RowsDeleteByIdsRequest(rowIds))
            .then(this.booleanCallback);
    }

    @bind
    public deleteRowsByQuery(baseName:string, tableName:string, query:QueryCondition):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.deleteRowsByQuery(organizationSid, environmentName, baseName, tableName, new RowsQueryRequest(query))
            .then(this.booleanCallback);
    }

    @bind
    public updateRowsByIds(baseName:string, tableName:string, dataFrame:IFrame):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.updateRowsByIds(organizationSid, environmentName, baseName, tableName, new RowsUpdateByIdsRequest(dataFrame))
            .then(this.booleanCallback);
    }

    @bind
    public updateRowsByQuery(baseName:string, tableName:string, query:QueryCondition, updateFrame:IFrame):Promise<ABoolean | AError>
    {
        const {organizationSid, environmentName} = this._currentContext();
        return API.rows.updateRowsByQuery(organizationSid, environmentName, baseName, tableName, new RowsUpdateByQueryRequest(query, updateFrame))
            .then(this.booleanCallback);
    }

    @bind
    private booleanCallback(response:any):ABoolean | AError
    {
        return response instanceof ApiError
            ? new AError(response.message || response.error)
            : ABoolean.true();
    }

    @bind
    private frameCallback<T>(extractFrameFn:(response:T) => any):((response:T) => IFrame | AError)
    {
        return (response) => response instanceof ApiError
            ? new AError(response.message || response.error)
            : Frames.deserializeFrameFromApi(extractFrameFn(response));
    }

    @bind
    private integerCallback<T>(extractNumberFn:(response:T) => number):((response:T) => AInteger | AError)
    {
        return (response) => response instanceof ApiError
            ? new AError(response.message || response.error)
            : new AInteger(extractNumberFn(response));
    }

    @bind
    private objectsCallback<T>(
        extractObjectsFn:(response:T) => Array<any>,
        extractFields:Array<string>,
        renameColumns:Array<string>):((response:T) => IFrame | AError)
    {
        return (response) =>
        {
            if( response instanceof ApiError )
                return new AError(response.message || response.error);

            const result = JsonUtil.convertJsonObjectToFrame(extractObjectsFn(response));

            const fields = result.getFieldsUnsafe();
            const data = result.getDataUnsafe();

            const extractFieldsInfo = extractFields
                .map((fieldName, index) => ({
                    name: renameColumns[index],
                    offset: fields.findIndex(f => f.name === fieldName),
                }))
                .filter(info => info.offset >= 0);

            const newFields = extractFieldsInfo.map(info =>
                new FrameField(info.name, fields[info.offset].type)
            );

            const newData = data.map(row =>
                extractFieldsInfo.map(info => row[info.offset])
            );

            return Frames.fromFieldsAndData(newFields, newData);
        };
    }
}
